<template>
  <section class="application-domains" aria-labelledby="application-domains-title">
    <div class="container py-5">
      <h2 id="application-domains-title" class="domains-title text-center">
        {{ $t("applicationDomains.title") }}
      </h2>

      <div
        class="domains-wrap"
        :class="selectedDomain < 4 ? 'selected-top' : 'selected-bottom'"
        :style="{ '--selected-color': domains[selectedDomain].color }"
      >
        <div
          class="domains-pop-box domains-pop-top"
          v-html="$t(`applicationDomains.item${selectedDomain + 1}Description`)"
        ></div>

        <div class="domains-grid">
          <div
            v-for="(domain, index) in domains"
            :key="domain.key"
            class="domain-item"
            :class="{ active: selectedDomain === index }"
            :style="{
              '--active-color': domain.color,
              '--mask-src': `url(${maskDown})`,
              '--mask-up-src': `url(${maskUp})`,
            }"
          >
            <button
              class="domain-button"
              type="button"
              :aria-pressed="selectedDomain === index"
              :aria-label="$t(`applicationDomains.item${index + 1}Title`)"
              @click="selectedDomain = index"
            >
              <picture>
                <source
                  v-if="domain.mobileSrc"
                  media="(max-width: 575.98px), (orientation: portrait)"
                  :srcset="domain.mobileSrc"
                />
                <img
                  class="domain-image"
                  :src="domain.src"
                  :alt="$t(`applicationDomains.item${index + 1}Title`)"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <span class="domain-color" aria-hidden="true"></span>
            </button>

            <div class="domain-label">
              {{ $t(`applicationDomains.item${index + 1}Title`) }}
            </div>

            <transition name="domain-description">
              <div
                v-if="selectedDomain === index"
                class="domain-copy domain-copy-desktop"
                v-html="$t(`applicationDomains.item${index + 1}Description`)"
              ></div>
            </transition>
          </div>
        </div>

        <div
          class="domains-pop-box domains-pop-bottom"
          v-html="$t(`applicationDomains.item${selectedDomain + 1}Description`)"
        ></div>

        <div
          class="domain-copy domain-copy-mobile"
          v-html="$t(`applicationDomains.item${selectedDomain + 1}Description`)"
        ></div>
      </div>
    </div>
  </section>
</template>

<script>
import domain01 from "@/assets/images/application-domains/domain-01.png";
import domain02 from "@/assets/images/application-domains/domain-02.png";
import domain03 from "@/assets/images/application-domains/domain-03.png";
import domain04 from "@/assets/images/application-domains/domain-04.png";
import domain05 from "@/assets/images/application-domains/domain-05.png";
import domain06 from "@/assets/images/application-domains/domain-06.png";
import domain07 from "@/assets/images/application-domains/domain-07.png";
import domain08 from "@/assets/images/application-domains/domain-08.png";
import domain01Mobile from "@/assets/images/application-domains/domain-01-mobile.png";
import domain02Mobile from "@/assets/images/application-domains/domain-02-mobile.png";
import domain03Mobile from "@/assets/images/application-domains/domain-03-mobile.png";
import domain04Mobile from "@/assets/images/application-domains/domain-04-mobile.png";
import maskDown from "@/assets/images/application-domains/mask-down.png";
import maskUp from "@/assets/images/application-domains/mask-up.png";

export default {
  name: "ApplicationDomains",
  data() {
    return {
      selectedDomain: 0,
      maskDown,
      maskUp,
      domains: [
        { key: "aesthetic", src: domain01, mobileSrc: domain01Mobile, color: "#170579" },
        { key: "ophthalmology", src: domain02, mobileSrc: domain02Mobile, color: "#173ed1" },
        { key: "dentistry", src: domain03, mobileSrc: domain03Mobile, color: "#3477d4" },
        { key: "orthopedics", src: domain04, mobileSrc: domain04Mobile, color: "#5cc7d6" },
        { key: "gynecology", src: domain05, color: "#61d1b2" },
        { key: "general", src: domain06, color: "#62d382" },
        { key: "regenerative", src: domain07, color: "#8ad240" },
        { key: "laboratory", src: domain08, color: "#b8d042" },
      ],
    };
  },
};
</script>

<style scoped>
.application-domains {
  background: #fff;
  padding-bottom: 11rem;
}

@media (min-width: 992px) {
  .application-domains {
    padding-bottom: 15rem;
  }

  .application-domains .container {
    padding-top: 6rem !important;
  }

  .domain-label {
    position: absolute;
    top: calc(100% + 0.625rem);
    left: 50%;
    width: max-content;
    max-width: 140px;
    min-height: 0;
    margin: 0;
    padding: 0.25rem 0.125rem;
    transform: translateX(-50%);
  }

  .application-domains .domain-copy-desktop {
    top: calc(100% + 4.75rem);
  }
}

.domains-title {
  margin-bottom: 2rem;
  color: #5fa4b8;
  font-size: 1.6rem;
  font-weight: 700;
}

.domains-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: visible;
}

.domains-pop-box {
  display: none;
}

.domains-grid {
  --overlap: 9px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: visible;
}

.domain-item {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: calc(var(--overlap) * -1);
  transition: transform 0.15s ease;
}

.domain-item:first-child {
  margin-left: 0;
}

.domain-item.active {
  z-index: 2;
  transform: translateY(-2px);
}

.domain-button {
  position: relative;
  display: block;
  width: 128px;
  padding: 0;
  overflow: visible;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.domain-button:focus-visible {
  border-radius: 50%;
  outline: 3px solid #5fa4b8;
  outline-offset: 4px;
}

.domain-image {
  display: block;
  width: 100%;
  height: auto;
}

.domain-button picture {
  display: block;
}

.domain-color {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: var(--active-color);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  -webkit-mask-image: var(--mask-src);
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center bottom;
  -webkit-mask-size: contain;
  mask-image: var(--mask-src);
  mask-repeat: no-repeat;
  mask-position: center bottom;
  mask-size: contain;
}

.domain-item.active .domain-color {
  opacity: 1;
}

.domain-label {
  width: 140px;
  min-height: 2.6rem;
  margin-top: 0.45rem;
  color: #333;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.25;
  text-align: center;
  white-space: pre-line;
}

.domain-copy {
  color: #555;
  font-size: 0.94rem;
  line-height: 1.7;
  text-align: center;
}

.domain-copy-desktop {
  position: absolute;
  top: calc(100% + 1rem);
  left: 50%;
  width: max-content;
  max-width: 360px;
  transform: translateX(-50%);
}

.domain-copy-mobile {
  display: none;
}

.domain-description-enter-active,
.domain-description-leave-active {
  transition: opacity 0.18s ease, transform 0.22s ease;
}

.domain-description-enter-from,
.domain-description-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}

@media (max-width: 991.98px) {
  .application-domains {
    padding-bottom: 0;
  }

  .domains-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1.25rem 0;
    max-width: 620px;
    margin: 0 auto;
  }

  .domain-item,
  .domain-item:first-child {
    min-width: 0;
    margin-left: 0;
  }

  .domain-button {
    width: min(128px, 23vw);
  }

  .domain-label {
    width: 100%;
    padding-inline: 0.25rem;
  }

  .domain-copy-desktop {
    display: none;
  }

  .domain-copy-mobile {
    display: block;
    max-width: 560px;
    min-height: 8rem;
    margin: 2rem auto 0;
  }
}

@media (max-width: 575.98px), (orientation: portrait) {
  .application-domains {
    padding-bottom: 0;
  }

  .application-domains .container {
    padding-top: 3rem !important;
    padding-inline: 0.75rem;
  }

  .domains-title {
    margin-bottom: 1.25rem;
  }

  .domains-pop-box {
    width: calc(100% - 1rem);
    padding: 1.15rem 0.75rem;
    border: 3px solid var(--selected-color);
    border-radius: 18px;
    background: #fff;
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.12);
    color: var(--selected-color);
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.45;
    text-align: center;
  }

  .selected-top .domains-pop-top,
  .selected-bottom .domains-pop-bottom {
    display: block;
  }

  .domains-pop-top {
    margin-bottom: 3.75rem;
  }

  .domains-pop-bottom {
    margin-top: 3.75rem;
  }

  .selected-bottom .domains-grid {
    margin-top: 3.5rem;
  }

  .domains-grid {
    --overlap: 6px;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    max-width: 440px;
    gap: 0;
  }

  .domain-item,
  .domain-item:first-child {
    flex: 0 0 25%;
    width: 25%;
    max-width: 25%;
    margin-left: 0;
  }

  .domain-item:not(:nth-child(4n + 1)) {
    margin-left: calc(var(--overlap) * -1);
  }

  .domain-button {
    width: min(110px, 24vw);
  }

  .domain-item:nth-child(-n + 4) .domain-color {
    -webkit-mask-image: var(--mask-up-src);
    mask-image: var(--mask-up-src);
  }

  .domain-label {
    position: absolute;
    left: 50%;
    width: max-content;
    max-width: 23vw;
    min-height: 0;
    margin: 0;
    padding: 0 0.1rem;
    transform: translateX(-50%);
    font-size: 0.9rem;
    line-height: 1.2;
  }

  .domain-item:nth-child(-n + 4) .domain-label {
    bottom: calc(100% + 0.55rem);
  }

  .domain-item:nth-child(n + 5) .domain-label {
    top: calc(100% + 0.55rem);
  }

  .domain-copy-mobile {
    display: none;
  }
}
</style>
